import { useCallback, useState } from 'react';
import { describeError } from '../describeError';
import { payrollHistoryImportGateway as gateway } from '../index';
import type { ValidationSummary } from '../types';
import { useAsyncData, type AsyncData } from './useAsyncData';
import { useIsMounted } from './useIsMounted';

export type ValidationAction = 'save' | 'revalidate';

export interface ValidationSession extends AsyncData<ValidationSummary> {
  busy: ValidationAction | null;
  actionError: string | null;
  setActionError: (message: string | null) => void;
  /** Runs a call that returns a fresh validation summary (save mappings, revalidate), shows it, then reloads the batch
   * so the status badge and the next-step links are current. Resolves to whether the call itself succeeded. */
  apply: (kind: ValidationAction, run: () => Promise<ValidationSummary>, message: (summary: ValidationSummary) => string) => Promise<boolean>;
}

/** Loads a batch's validation summary and runs the calls that change it. Shared by the steps that show it. */
export function useValidationSession(
  batchId: string,
  announce: (message: string) => void,
  refreshBatch: () => Promise<void>,
): ValidationSession {
  const mounted = useIsMounted();
  const load = useCallback((signal: AbortSignal) => gateway.getValidation(batchId, signal), [batchId]);
  const data = useAsyncData(load);
  const { setData } = data;
  const [busy, setBusy] = useState<ValidationAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const apply: ValidationSession['apply'] = async (kind, run, message) => {
    setBusy(kind);
    setActionError(null);
    let summary: ValidationSummary;
    try {
      summary = await run();
    } catch (err) {
      if (mounted.current) {
        setActionError(describeError(err, kind === 'save' ? 'Could not save the mappings.' : 'Could not revalidate this import.'));
        setBusy(null);
      }
      return false;
    }
    if (!mounted.current) return true;
    setData(summary);
    try {
      await refreshBatch();
    } catch {
      // The save/revalidate itself succeeded; only the follow-up reload failed. Say exactly that.
      if (mounted.current) {
        setActionError('Saved, but the latest status could not be reloaded. Refresh the page to see it.');
        setBusy(null);
      }
      return true;
    }
    if (mounted.current) {
      announce(message(summary));
      setBusy(null);
    }
    return true;
  };

  return { ...data, busy, actionError, setActionError, apply };
}
